import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import type { WasteFilters } from "@/hooks/use-waste-filters";
import type { WasteType } from "@/lib/waste-types";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type WasteStatsBucket = {
  key: string;
  event_count: number;
  total_weight: number;
  total_cost: number;
};

export type WasteStats = {
  total_events: number;
  total_weight: number;
  total_cost: number;
  by_waste_type: WasteStatsBucket[];
  by_classification_level: WasteStatsBucket[];
  by_service_time: WasteStatsBucket[];
  by_category: WasteStatsBucket[];
  by_day: WasteStatsBucket[];
  /** One bucket per L1 diagnostic reason (key = reason id), ordered by frequency. */
  by_l1_reason: WasteStatsBucket[];
  /** One bucket per service × site-local day-of-week (key = '<service>:<isodow>', 1 = Monday). */
  by_service_dow: WasteStatsBucket[];
};

export function useWasteStats(
  filters: WasteFilters,
  dateRange: { from: string | null; to: string | null },
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    /** Waste type removed from every bucket — the Dashboard passes "packaging"
     * for its food-only universe (POS2-861). An explicit `filters.waste_type`
     * wins: the user deliberately inspecting one type sees that type. */
    excludeWasteType?: WasteType;
    /** Restrict the response to these bucket dims (POS2-960) so a large-window
     * call skips the aggregate passes + joins it never reads. Omit = all dims. */
    dims?: WasteStatsDim[];
  },
) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const params = buildStatsParams(filters, dateRange, options?.excludeWasteType, options?.dims);

  return useQuery<WasteStats>({
    queryKey: ["waste-stats", params.toString()],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/stats?${params.toString()}`, {
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: WasteStats };
      return normalizeWasteStats(body.data);
    },
    enabled: (options?.enabled ?? true) && !!headers.Authorization.replace("Bearer ", ""),
    refetchInterval: options?.refetchInterval,
    placeholderData: (prev) => prev,
  });
}

/**
 * Boundary normalisation for a raw stats payload. Deploy skew (POS2-922) means a
 * backend that predates a newer bucket field omits it — a raw `undefined` array
 * then crashes consumers (the heatmap iterates `by_service_dow`, POS2-873). Default
 * the newer optional bucket to [] here so widgets render empty, never throw.
 */
export function normalizeWasteStats(raw: WasteStats): WasteStats {
  return { ...raw, by_service_dow: raw.by_service_dow ?? [] };
}

/** The bucket dimensions `waste_event_stats` can return (POS2-960). */
export type WasteStatsDim =
  | "waste_type"
  | "classification_level"
  | "service_time"
  | "category"
  | "day"
  | "l1_reason"
  | "service_dow"
  | "total";

export function buildStatsParams(
  filters: WasteFilters,
  dateRange: { from: string | null; to: string | null },
  excludeWasteType?: WasteType,
  dims?: WasteStatsDim[],
): URLSearchParams {
  const params = new URLSearchParams();
  if (dateRange.from) params.set("date_from", dateRange.from);
  if (dateRange.to) params.set("date_to", dateRange.to);
  if (filters.site_id) params.set("site_id", filters.site_id);
  if (filters.kitchen_id) params.set("kitchen_id", filters.kitchen_id);
  if (filters.device_serial) params.set("device_serial", filters.device_serial);
  if (filters.waste_type) params.set("waste_type", filters.waste_type);
  if (filters.classification_level != null) {
    params.set("classification_level", String(filters.classification_level));
  }
  if (filters.status) params.set("status", filters.status);
  if (filters.service_time) params.set("service_time", filters.service_time);
  if (filters.is_confirmed != null) params.set("is_confirmed", String(filters.is_confirmed));
  // Site-local "HH:MM" time-of-day window, applied within each day (POS2-1483).
  if (filters.time_from) params.set("time_from", filters.time_from);
  if (filters.time_to) params.set("time_to", filters.time_to);
  // An explicit waste_type filter suppresses the exclusion — the user picked a
  // type to inspect (possibly packaging itself), and equality already narrows.
  if (excludeWasteType && !filters.waste_type) {
    params.set("exclude_waste_type", excludeWasteType);
  }
  // Repeated `dims` params → FastAPI list[str]; omit entirely for the all-dims default.
  dims?.forEach((d) => params.append("dims", d));
  return params;
}
